import { Component, OnInit, OnDestroy } from '@angular/core';
import { Params, ActivatedRoute, Router } from '@angular/router';
import { UntypedFormGroup, UntypedFormControl, Validators, FormArray, ValidationErrors } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NgbDateStruct } from '@ng-bootstrap/ng-bootstrap';

import { AppConstants } from 'appshared/app-constants';
import * as Models from 'appshared/shared-models';
import { MiscTools } from 'appshared/misc-tools';
import { ValidationTools } from 'appshared/validation-tools';
import { TextTools } from 'appshared/text-tools';
import { SharedLicenseTools } from 'appshared/shared-license-tools';
import { LicenseValidationTools } from 'appshared/license-validation-tools';

import { AuthService } from 'client/app/services/auth.service';
import { LicensingService } from '../licensing.service';
import { LicensingAdminService } from '../../licensing-admin/licensing-admin.service';
import { UiAlertsService } from 'client/app/components/ui-alerts/ui-alerts.service';

@Component({
	selector: 'app-commercial-form',
	templateUrl: './commercial-form.component.html',
	styleUrls: ['./commercial-form.component.scss']
})
export class CommercialFormComponent implements OnInit, OnDestroy {
	ac = AppConstants;

	id: number; // activation ID
	activation: Models.LPActivation;
	guess: string = '';

	// 'standard' edit stuff

	theForm: UntypedFormGroup;
	loading = true;
	saving = false;

	staffUserSelections: any[] = [];

	usersWithAccess: Models.User[] = [];

	// other stuff
	private userSubscription: Subscription;
	authUser: Models.AuthUser;

	commercialTypeSelections: any[] = [];

	protocolReady: boolean = false;
	availableSets: Models.ProtocolSet[] = [];
	linkedProtocolSets: Models.ProtocolSet[] = [];
	linkedProtocolSetInForm: Models.ProtocolSet[] = [];
	nicePsetProjected: any = {};

	// *************************************************************************************
	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private authService: AuthService,
		private uiAlertsService: UiAlertsService,
		private licensingAdminService: LicensingAdminService,
		private licensingService: LicensingService) {
		this.route.paramMap.subscribe(params => {
			this.id = +params.get('id');
			this.guess = params.get('guess');
		});

	}

	// *************************************************************************************
	ngOnInit(): void {
		this.userSubscription = this.authService.user.subscribe((authUser) => {
			this.authUser = authUser;
			if (!ValidationTools.checkAccess(this.authUser, 'manage-commercial')) {
				this.onCancel();
				return;
			};
			this.loadData();
		});
	}

	// *************************************************************************************
	ngOnDestroy() {
		if (this.userSubscription) this.userSubscription.unsubscribe();
	}

	// *************************************************************************************
	async loadData() {

		this.commercialTypeSelections = [];
		this.commercialTypeSelections.push({
			value: '',
			label: 'Not configured',
			short_label: 'Not configured',
			acronym: '',
			info: ''
		});
		for (const keyCommercialType of AppConstants.keyCommercialTypes)
			this.commercialTypeSelections.push(keyCommercialType);

		this.activation = await this.licensingService.getOne(this.id);
		if (!this.activation || this.activation == null || this.activation.id === 0) {
			this.router.navigate([AppConstants.urls.notfound]);
			return;
		} // if

		const product: Models.LicenseProduct = await this.licensingAdminService.getProductByName(this.activation.product);
		if (!product) {
			this.router.navigate([AppConstants.urls.notfound]);
			return;
		}

		this.protocolReady = product.show_protocols > 0;

		if (this.guess != null && this.guess !== '' && (this.activation.commercial_type == null || this.activation.commercial_type === ''))
			this.activation.commercial_type = this.guess;

		const allSets: Models.ProtocolSet[] = await this.licensingAdminService.getProtocolSets();
		this.availableSets = [];
		this.linkedProtocolSets = [];
		this.linkedProtocolSetInForm = [];

		let linkedSets: Models.ActivationProtocolSet[] = await this.licensingService.getActivationProtocolSets(this.id);

		for (const pSet of allSets) {
			const idx = MiscTools.findIndexGeneric(linkedSets, 'set_id', pSet.id);
			if (idx !== -1) {
				pSet['projected'] = linkedSets[idx].projected;
				pSet['exclude_billing_code'] = linkedSets[idx].exclude_billing_code;
				this.linkedProtocolSets.push(pSet);
			} else {
				pSet['projected'] = 0;
				pSet['exclude_billing_code'] = '';
				this.availableSets.push(pSet);
			} // for
		} // for

		this.theForm = new UntypedFormGroup({
			commercial_type: new UntypedFormControl(this.activation.commercial_type),
			commercial_info: new UntypedFormControl(this.activation.commercial_info),
		});

		for (const pset of this.linkedProtocolSets) {
			this.addProtocolSetToForm(pset);
		}

		this.loading = false;
	}

	// *************************************************************************************
	async onSubmit() {
		this.saving = true;
		const errors: string[] = [];

		let commercialType: string = this.theForm.value.commercial_type;
		if (commercialType == null) commercialType = '';

		let commercialInfo: string = this.theForm.value.commercial_info;
		if (commercialInfo == null) commercialInfo = '';

		const linkedSesToAddt: Models.ActivationProtocolSet[] = [];

		for (const pSet of this.linkedProtocolSetInForm) {
			let projected: number = +this.theForm.value['pset_projected_' + pSet.id];
			if (isNaN(projected)) projected = 0;

			let exclude_billing_code: string = '';
			if (this.theForm.value['pset_exclude_billing_code_' + pSet.id]) exclude_billing_code = this.theForm.value['pset_exclude_billing_code_' + pSet.id];

			if (exclude_billing_code && exclude_billing_code.trim() !== '') {
				const matchingCodes: Models.BillingCode[] = await this.licensingService.getBillingCodes([exclude_billing_code]);
				if (matchingCodes.length === 0)
					errors.push('"' + exclude_billing_code + '" does not appear to be a valid billing code.');
			} // if

			const linkedSet: Models.ActivationProtocolSet = new Models.ActivationProtocolSet();
			linkedSet.activation_id = this.id;
			linkedSet.set_id = pSet.id;
			linkedSet.data_type = 'protocol-data';
			linkedSet.projected = projected;
			linkedSet.exclude_billing_code = exclude_billing_code;

			linkedSesToAddt.push(linkedSet);
		} // for

		if (errors.length > 0) {
			this.uiAlertsService.addMsgs(errors, 'warning', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
			this.saving = false;
			return;
		} // if

		try {
			await this.licensingService.setKeyCommercial(this.id, commercialType, commercialInfo);

			for (const linkedSet of linkedSesToAddt)
				await this.licensingService.addActivationProtocolSet(linkedSet);

			// get a fresh copy of linked sets...
			const linkedSets: Models.ActivationProtocolSet[] = await this.licensingService.getActivationProtocolSets(this.id);
			// delete any that weren't in the form...
			for (const lSet of linkedSets) {
				const idx = MiscTools.findIndex(this.linkedProtocolSetInForm, lSet.set_id);
				if (idx === -1)
					await this.licensingService.deleteActivationProtocolSet(lSet)
			} // for

			this.onCancel();
		} catch (e: any) {
			this.saving = false;
			this.uiAlertsService.addMsg(e.message, 'danger', '', false, AppConstants.standardMessageAutoCloseTimeSecs);
		}
	}

	// *************************************************************************************
	onCancel() {
		this.router.navigate(['/' + AppConstants.urls.licensing, 'activation', this.id], { queryParams: { tabID: 'nav-commercial-tab' } });
	}

	// *************************************************************************************
	addProtocolSetFromPickList() {
		const picker = document.getElementById('addPsetPicker');
		if (picker) {
			let id: number = +picker['value'];
			if (isNaN(id)) id = 0;
			if (id !== 0) {
				const idx = MiscTools.findIndexGeneric(this.availableSets, 'id', id);
				if (idx !== -1) {
					const toAdd = MiscTools.deepClone(this.availableSets[idx]);
					this.availableSets.splice(idx, 1);
					this.addProtocolSetToForm(toAdd);
				} // if
			} // if
		}
	}

	// *************************************************************************************
	addProtocolSetFromDropDown(pSet: Models.ProtocolSet) {
		const idx = MiscTools.findIndexGeneric(this.availableSets, 'id', pSet.id);
		if (idx !== -1) this.availableSets.splice(idx, 1);
		this.addProtocolSetToForm(pSet);
	}

	// *************************************************************************************
	addProtocolSetToForm(pSet: Models.ProtocolSet) {
		if (pSet) {
			let projected: number = 0;
			if (pSet['projected']) projected = +pSet['projected'];
			if (isNaN(projected)) projected = 0;

			let exclude_billing_code: string = '';
			if (pSet['exclude_billing_code']) exclude_billing_code = pSet['exclude_billing_code'];

			this.theForm.addControl('pset_projected_' + pSet.id, new UntypedFormControl(projected, [Validators.max(AppConstants.maxMeterLimit)]));
			this.theForm.addControl('pset_exclude_billing_code_' + pSet.id, new UntypedFormControl(exclude_billing_code));
			this.linkedProtocolSetInForm.push(pSet);
			this.onProtocolSetProjectedChange(pSet.id);
		} // if
	}
	// *************************************************************************************
	removeProtocolSetFromForm(id: number) {
		const idx = MiscTools.findIndexGeneric(this.linkedProtocolSetInForm, 'id', id);
		if (idx !== -1 && confirm('Are you sure?')) {
			let id: number = this.linkedProtocolSetInForm[idx].id;
			this.linkedProtocolSetInForm.splice(idx, 1);
			this.theForm.removeControl('pset_projected_' + id);
			this.theForm.removeControl('pset_exclude_billing_code_' + id);
		} // if
	}
	// *************************************************************************************
	onProtocolSetProjectedChange(id: number) {
		let projected = +this.theForm.value['pset_projected_' + id];
		if (isNaN(projected)) projected = 0;
		if (projected > AppConstants.maxMeterLimit) {
			projected = AppConstants.maxMeterLimit;
			this.theForm.controls['pset_projected_' + id].setValue(AppConstants.maxMeterLimit);
		} // if

		if (projected > 0)
			this.nicePsetProjected[id] = TextTools.formattedMB(projected);
		else
			this.nicePsetProjected[id] = '';
	}
}
